import { useCallback, useEffect } from "react";

import { useUser } from "../../entities/users/hooks";
import { actions } from "../../entities/slices/user.slice";
import { useAppDispatch } from "../../entities/utils/useAppDispatch.hook";
import { useFeature } from "../features";
import { getDocument, getMatchMediaWindow } from "../../../utils/web";

import { useSetUserAccessibilityPreferences } from "./requests";
import {
  COLOR_MODE_CONTRAST_OPTIONS,
  DEFAULT_ACCESSIBILITY_PREFERENCES,
  type ColorModeContrastPreference,
} from "./types";

type ColorModeContrast = "less" | "more";

/**
 * Resolves the effective contrast value for a user based on
 * feature flag and user preference.
 */
export const resolveContrast = (
  featureOn: boolean,
  pref: ColorModeContrastPreference,
): ColorModeContrast => {
  if (!featureOn) return "less";
  if (pref === "more") return "more";
  if (pref === "system") {
    const w = getMatchMediaWindow();
    if (!w) return "less";
    return w.matchMedia("(prefers-contrast: more)").matches ? "more" : "less";
  }
  return "less";
};

/**
 * Read-only hook that resolves the user's contrast preference and writes
 * `data-prefers-contrast` on the <html> element.
 */
export const useApplyColorModeContrast = () => {
  const { featureEnabled } = useFeature();
  const user = useUser();

  const preference: ColorModeContrastPreference =
    user?.user_accessibility_preferences?.color_mode_contrast ?? "system";

  const resolved = resolveContrast(
    featureEnabled("accessibility_mode_contrast"),
    preference,
  );

  useEffect(() => {
    const doc = getDocument();
    if (!doc) return;
    doc.documentElement.dataset.prefersContrast = resolved;
  }, [resolved]);

  return { resolved, preference };
};

/**
 * Full contrast hook: composes the read-only DOM effect with an optimistic
 * setter that persists the user's preference.
 */
export const useColorModeContrast = () => {
  const { resolved, preference } = useApplyColorModeContrast();
  const user = useUser();
  const dispatch = useAppDispatch();
  const { setUserAccessibilityPreferences } =
    useSetUserAccessibilityPreferences();

  // setter for UI to update preferences
  const setColorModeContrast = useCallback(
    (value: ColorModeContrastPreference) => {
      // PUT is fire-and-forget and does not sync its response back into Redux.
      // Treat this as an optimistic update
      if (user) {
        dispatch(
          actions.upsert({
            ...user,
            user_accessibility_preferences: {
              ...DEFAULT_ACCESSIBILITY_PREFERENCES,
              ...user.user_accessibility_preferences,
              color_mode_contrast: value,
            },
          }),
        );
      }
      setUserAccessibilityPreferences({ color_mode_contrast: value });
    },
    [dispatch, setUserAccessibilityPreferences, user],
  );

  return {
    resolved,
    preference,
    options: COLOR_MODE_CONTRAST_OPTIONS,
    setColorModeContrast,
  };
};
